import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import * as Dialog from '@radix-ui/react-dialog';
import { useDucatClient } from '@/context/DucatClientCtx';
import { WalletType, WalletTypeExtended } from '@/context/types';
import WalletList from '@/components/wallet-list';
import { Button } from '@/components/button';
import { Icons } from '@/components/icons';
import ButtonSpinner from '@/components/button-spinner';
import { cn } from '@/utils/cn';
import { usePreventMultipleClicks } from '@/hooks/usePreventMultipleClicks';

interface WalletConnectButtonProps {
  mobileStylesOn: boolean;
  isSmall?: boolean;
}

const WalletConnectButton: React.FC<WalletConnectButtonProps> = ({ mobileStylesOn, isSmall = false }: WalletConnectButtonProps) => {
  const { client, contract, isContractLoading, resetWalletError, walletErrorMessage } = useDucatClient();
  const [open, setOpen] = useState<boolean>(false);

  // use custom hook for preventing multiple clicks on wallet connection
  const { isButtonBlocked, blockButton, unblockButton } = usePreventMultipleClicks();

  useEffect(() => {
    if (walletErrorMessage) {
      setOpen(false);
    }
  }, [walletErrorMessage]);

  const handleConnect = async (walletType: WalletType | WalletTypeExtended) => {
    // prevent multiple clicks
    if (isButtonBlocked) {
      return;
    }

    blockButton(); // block button immediately

    try {
      await client.connect(walletType as WalletType);
      unblockButton(); // unblock button on success
    } catch (error) {
      toast('Failed to Connect to Wallet. Please Try Again.');
      console.log(`Error: Failed to Connect to Wallet`, error);
      unblockButton(); // unblock button on error
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.DialogTrigger asChild>
        <Button
          leadingIcon={
            isContractLoading ? (
              <ButtonSpinner isSmall={isSmall} />
            ) : (
              <Icons.wallet
                className={cn(
                  mobileStylesOn ? 'h-[18px] w-[18px] lg:h-[24px] lg:w-[24px]' : 'h-[24px] w-[24px]',
                  isSmall && 'h-[18px] w-[18px]',
                )}
              />
            )
          }
          variant='secondary'
          className={cn(
            'focus-visible:ring-1 focus-visible:ring-border-secondary',
            mobileStylesOn
              ? 'text-sm lg:text-base py-1.5 lg:py-2 px-2 lg:px-4 gap-1 lg:gap-2 leading-4 lg:leading-6'
              : 'text-base py-2 px-4 gap-2 leading-6',
            isSmall && 'text-sm py-1.5 px-2 gap-1 leading-4',
          )}
          disabled={!contract}
          onClick={resetWalletError}
        >
          <span className={mobileStylesOn ? 'sr-only lg:not-sr-only' : ''}>Connect Wallet</span>
        </Button>
      </Dialog.DialogTrigger>
      <Dialog.Portal>
        <Dialog.Overlay className='fixed inset-0 bg-black/50 animate-fade-in z-50' />
        <Dialog.Content className='flex flex-col min-h-[75%] lg:h-full w-full lg:w-[530px] fixed bottom-0 right-0 rounded-t-10 lg:rounded-none border-t lg:border-l-2 border-border-secondary lg:border-border-primary bg-surface-secondary lg:bg-surface-primary animate-fade-in z-50 outline-none'>
          <Dialog.Description className='sr-only'>Choose a wallet to connect to the Ducat protocol</Dialog.Description>
          <div className={cn('flex items-center justify-between p-4 lg:px-8 lg:py-6 border-b border-border-primary lg:border-b-0')}>
            <Dialog.Title className='flex-1 text-text-primary lg:tracking-02em leading-6lg:leading-7 text-[22px] lg:text-2xl lg:text-center font-bold lg:font-normal'>
              Connect Wallet
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant='tertiary' className='bg-transparent'>
                <Icons.close />
              </Button>
            </Dialog.Close>
            <div className='lg:hidden absolute top-0 left-1/2 transform -translate-x-1/2 w-9 h-[5px] bg-border-primary rounded-full mt-1.5' />
          </div>
          <div className='flex-1 flex overflow-x-auto'>
            <WalletList handleConnect={handleConnect} isButtonBlocked={isButtonBlocked} />
          </div>
          {/* TODO: Fix later  */}
          {/* <div>
            <div className="w-full border-t-2 border-border-primary flex items-center justify-center gap-6 h-[100px] lg:px-20 px-4 shrink-0 fixed max-w-[530px] bottom-0 bg-surface-primary z-50">
              {activeWallet ? (
                <Button
                  className="bg-transparent"
                  variant="secondary"
                  onClick={() => setActiveWallet(null)}
                >
                  Back
                </Button>
              ) : (
                <Button className="bg-transparent" variant="secondary">
                  Get a wallet
                </Button>
              )}
            </div>
          </div> */}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default WalletConnectButton;
